
import React, { useState, RefObject } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from 'lucide-react';
import { generateCycleLengthOptions, generatePeriodLengthOptions } from './periodCalculatorUtils';
import { PeriodPrediction } from './periodCalculatorTypes';

interface PeriodCalculatorFormProps {
  onCalculate: (lastPeriod: string, cycleLength: string, periodLength: string) => void;
  calculatorRef: RefObject<HTMLDivElement>;
  results: {
    predictions?: PeriodPrediction[];
    message?: string;
  };
}

const PeriodCalculatorForm: React.FC<PeriodCalculatorFormProps> = ({ 
  onCalculate, 
  calculatorRef,
  results 
}) => {
  const [lastPeriod, setLastPeriod] = useState('');
  const [cycleLength, setCycleLength] = useState('28');
  const [periodLength, setPeriodLength] = useState('5');

  // Generate options for cycle length and period length
  const cycleLengthOptions = generateCycleLengthOptions();
  const periodLengthOptions = generatePeriodLengthOptions();

  const handleCalculateClick = () => {
    onCalculate(lastPeriod, cycleLength, periodLength);
  };

  return (
    <div className="grid gap-4">
      <div className="grid gap-2">
        <Label htmlFor="lastPeriod">First Day of Last Period</Label>
        <div className="flex">
          <Input
            id="lastPeriod"
            type="date"
            value={lastPeriod}
            onChange={(e) => setLastPeriod(e.target.value)}
            className="flex-1 focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none"
          />
          <div className="ml-2 bg-muted w-10 flex items-center justify-center rounded-md">
            <Calendar className="h-4 w-4" />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Enter the first day of your most recent period.
        </p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="cycleLength">Average Cycle Length</Label>
        <Select 
          value={cycleLength} 
          onValueChange={setCycleLength}
        >
          <SelectTrigger className="focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none">
            <SelectValue placeholder="Select cycle length" />
          </SelectTrigger>
          <SelectContent>
            {cycleLengthOptions.map(option => (
              <SelectItem key={option} value={option}>
                {option} days
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          The length of your menstrual cycle (from first day of one period to the first day of the next).
        </p>
      </div>

      <div className="grid gap-2">
        <Label htmlFor="periodLength">Average Period Length</Label>
        <Select 
          value={periodLength} 
          onValueChange={setPeriodLength}
        >
          <SelectTrigger className="focus:ring-2 focus:ring-primary focus:border-primary focus:outline-none">
            <SelectValue placeholder="Select period length" />
          </SelectTrigger>
          <SelectContent>
            {periodLengthOptions.map(option => (
              <SelectItem key={option} value={option}>
                {option} days
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          The typical duration of your menstrual flow.
        </p>
      </div>
      
      <Button onClick={handleCalculateClick} className="mt-2 focus:ring-2 focus:ring-offset-2 focus:ring-primary focus:outline-none">
        Calculate
      </Button>
    </div>
  );
};

export default PeriodCalculatorForm;
